import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api from "../../services/api";
import LoadingSkeleton from "../../components/common/LoadingSkeleton";
import dayjs from "dayjs";

// ─── 보고서 타입 정의 ──────────────────────────
const REPORT_TYPES = [
  { value: "store", label: "상가용 (간단형)" },
  { value: "logistics", label: "고물량 물류센터용 (세부형)" },
  { value: "government", label: "지자체 제출용 (공공형)" },
];

// ─── 품질등급 계산 (혼입률 기반 시뮬레이션) ─────
function getQualityGrade(data) {
  if (!data || data.grandTotal === 0) return "N/A";
  // 현재 혼입률 필드가 없으므로 총량 기준 간이 등급
  if (data.grandTotal >= 100) return "A";
  if (data.grandTotal >= 50) return "B";
  return "C";
}

// ─── 숫자 포맷 ──────────────────────────────────
function fmt(val) {
  if (val === null || val === undefined) return "0.0";
  return Number(val).toFixed(1);
}

function fmtKg(val) {
  return `${fmt(val)} kg`;
}

// ═══════════════════════════════════════════════════
// 메인 컴포넌트
// ═══════════════════════════════════════════════════
function ReportPage() {
  const [reportType, setReportType] = useState(0); // 탭 인덱스
  const [year, setYear] = useState(dayjs().year());
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [collectors, setCollectors] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedCollector, setSelectedCollector] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  // 수거자/이용자 목록 로드
  useEffect(() => {
    const fetchLists = async () => {
      try {
        setListLoading(true);
        const [colRes, userRes] = await Promise.all([
          api.get("/api/users/role/collector"),
          api.get("/api/users/role/user"),
        ]);
        setCollectors(
          (colRes.data || []).map((u) => ({
            id: u.userId,
            name: u.name,
            email: u.email,
          }))
        );
        setUsers(
          (userRes.data || []).map((u) => ({
            id: u.userId,
            name: u.name,
            email: u.email,
          }))
        );
      } catch (err) {
        console.error("목록 로드 실패:", err);
      } finally {
        setListLoading(false);
      }
    };
    fetchLists();
  }, []);

  // 보고서 데이터 조회
  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setReportData(null);

      const params = { year, month };
      if (selectedCollector) params.collectorId = selectedCollector;
      else if (selectedUser) params.userId = selectedUser;

      const res = await api.get("/api/dashboard/report", { params });
      setReportData(res.data);
    } catch (err) {
      setError("보고서 데이터를 불러오는 데 실패했습니다.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [year, month, selectedCollector, selectedUser]);

  // ─── PDF 다운로드 ─────────────────────────────
  const handleDownloadPdf = async () => {
    if (!reportData) return;
    setPdfLoading(true);

    try {
      const doc = new jsPDF("p", "mm", "a4");
      const typeLabel = REPORT_TYPES[reportType].label;
      const pageWidth = doc.internal.pageSize.getWidth();

      // ─── 한글 폰트 로드 ─────────────────────────
      const [regularRes, boldRes] = await Promise.all([
        fetch("/fonts/NanumGothic-Regular.ttf"),
        fetch("/fonts/NanumGothic-Bold.ttf"),
      ]);
      const regularBuf = await regularRes.arrayBuffer();
      const boldBuf = await boldRes.arrayBuffer();

      const toBase64 = (buf) => {
        const bytes = new Uint8Array(buf);
        let binary = "";
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
      };

      doc.addFileToVFS("NanumGothic-Regular.ttf", toBase64(regularBuf));
      doc.addFont("NanumGothic-Regular.ttf", "NanumGothic", "normal");
      doc.addFileToVFS("NanumGothic-Bold.ttf", toBase64(boldBuf));
      doc.addFont("NanumGothic-Bold.ttf", "NanumGothic", "bold");
      doc.setFont("NanumGothic");

      // autoTable 기본 스타일에도 폰트 적용
      const tableFont = "NanumGothic";

      // 헤더
      doc.setFont("NanumGothic", "bold");
      doc.setFontSize(18);
      doc.setTextColor(42, 154, 198);
      doc.text("이룸그린테크", 14, 20);

      doc.setFont("NanumGothic", "normal");
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      doc.text(`보고서 유형: ${typeLabel}`, 14, 30);
      doc.text(
        `기간: ${reportData.year}년 ${String(reportData.month).padStart(2, "0")}월`,
        14,
        37
      );
      doc.text(
        `담당자: ${reportData.collectorName || "전체"}`,
        14,
        44
      );
      doc.text(
        `연락처: ${reportData.collectorPhone || "-"}`,
        14,
        51
      );
      doc.setFontSize(9);
      doc.setTextColor(130, 130, 130);
      doc.text(
        `생성일: ${dayjs().format("YYYY-MM-DD HH:mm")}`,
        pageWidth - 60,
        20
      );

      doc.setDrawColor(42, 154, 198);
      doc.setLineWidth(0.5);
      doc.line(14, 55, pageWidth - 14, 55);

      let yPos = 63;

      // ─── 보고서 타입별 내용 생성 ─────────────────
      if (reportType === 0) {
        yPos = generateStoreReport(doc, reportData, yPos, pageWidth, tableFont);
      } else if (reportType === 1) {
        yPos = generateLogisticsReport(doc, reportData, yPos, pageWidth, tableFont);
      } else {
        yPos = generateGovernmentReport(doc, reportData, yPos, pageWidth, tableFont);
      }

      // 하단 서명란
      if (yPos > doc.internal.pageSize.getHeight() - 45) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFont("NanumGothic", "normal");
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text("이룸그린테크 담당자: ________________", 14, yPos + 10);
      doc.text(`날짜: ${dayjs().format("YYYY-MM-DD")}`, 14, yPos + 18);
      doc.text("확인 (서명): ________________", pageWidth / 2, yPos + 10);

      const fileName = `이룸_보고서_${reportData.year}-${String(reportData.month).padStart(2, "0")}_${reportData.collectorName || "전체"}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error("PDF 생성 실패:", err);
      setError("PDF 생성 중 오류가 발생했습니다.");
    } finally {
      setPdfLoading(false);
    }
  };

  // ─── 연도/월 옵션 ─────────────────────────────
  const yearOptions = [];
  for (let y = 2024; y <= dayjs().year() + 1; y++) yearOptions.push(y);
  const monthOptions = [];
  for (let m = 1; m <= 12; m++) monthOptions.push(m);

  // ═══════════════════════════════════════════════
  // 렌더
  // ═══════════════════════════════════════════════
  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        보고서 관리
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        수거 데이터를 기반으로 3종 보고서를 생성하고 PDF로 다운로드할 수
        있습니다.
      </Typography>

      {/* ─── 보고서 타입 탭 ─────────────────────── */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={reportType}
          onChange={(_, v) => setReportType(v)}
          variant="fullWidth"
          sx={{
            "& .MuiTab-root": { fontWeight: 600, py: 1.5 },
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {REPORT_TYPES.map((t, idx) => (
            <Tab key={t.value} label={t.label} />
          ))}
        </Tabs>
      </Card>

      {/* ─── 필터 영역 ─────────────────────────── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>연도</InputLabel>
                <Select
                  value={year}
                  label="연도"
                  onChange={(e) => setYear(e.target.value)}
                >
                  {yearOptions.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}년
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>월</InputLabel>
                <Select
                  value={month}
                  label="월"
                  onChange={(e) => setMonth(e.target.value)}
                >
                  {monthOptions.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}월
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>수거자 선택</InputLabel>
                <Select
                  value={selectedCollector}
                  label="수거자 선택"
                  onChange={(e) => {
                    setSelectedCollector(e.target.value);
                    if (e.target.value) setSelectedUser("");
                  }}
                  disabled={listLoading}
                >
                  <MenuItem value="">전체</MenuItem>
                  {collectors.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name} ({c.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>이용자 선택</InputLabel>
                <Select
                  value={selectedUser}
                  label="이용자 선택"
                  onChange={(e) => {
                    setSelectedUser(e.target.value);
                    if (e.target.value) setSelectedCollector("");
                  }}
                  disabled={listLoading}
                >
                  <MenuItem value="">전체</MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<SearchIcon />}
                onClick={fetchReport}
                disabled={loading}
                sx={{ height: 40 }}
              >
                조회
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ─── 에러 ──────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* ─── 로딩 ──────────────────────────────── */}
      {loading && <LoadingSkeleton variant="table" />}

      {/* ─── 보고서 미리보기 ───────────────────── */}
      {reportData && !loading && (
        <Card>
          <CardContent>
            {/* 상단 액션 바 */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DescriptionIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  {REPORT_TYPES[reportType].label} 미리보기
                </Typography>
                <Chip
                  label={`${reportData.year}년 ${reportData.month}월`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <Button
                variant="contained"
                startIcon={
                  pdfLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={handleDownloadPdf}
                disabled={pdfLoading}
              >
                {pdfLoading ? "생성 중..." : "PDF 다운로드"}
              </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* 보고서 본문 */}
            {reportType === 0 && <StorePreview data={reportData} />}
            {reportType === 1 && <LogisticsPreview data={reportData} />}
            {reportType === 2 && <GovernmentPreview data={reportData} />}
          </CardContent>
        </Card>
      )}

      {/* 데이터 없을 때 안내 */}
      {!reportData && !loading && !error && (
        <Card>
          <CardContent
            sx={{
              textAlign: "center",
              py: 8,
              color: "text.secondary",
            }}
          >
            <DescriptionIcon sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6">
              연도, 월, 대상을 선택한 후 조회 버튼을 눌러주세요
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              수거 데이터를 기반으로 보고서가 생성됩니다.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

// ═══════════════════════════════════════════════════
// 1) 상가용 미리보기
// ═══════════════════════════════════════════════════
function StorePreview({ data }) {
  const ts = data.totalSummary || {};
  return (
    <Box>
      {/* 기본 정보 */}
      <SectionTitle number="1" title="기본 정보" />
      <InfoGrid
        items={[
          { label: "담당자", value: data.collectorName || "전체" },
          { label: "연락처", value: data.collectorPhone || "-" },
          {
            label: "기록 기간",
            value: `${data.year}년 ${data.month}월`,
          },
          { label: "수거 장소 수", value: `${data.locations?.length || 0}곳` },
        ]}
      />

      {/* 장소별 배출량 기록 */}
      {(data.locations || []).map((loc, idx) => (
        <Box key={loc.locationId} sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            📍 {loc.locationName} ({loc.address})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            소유자: {loc.ownerName} | 수거 요일: {loc.locationDay} | 시간:{" "}
            {loc.locationTime || "-"}
          </Typography>

          <SectionTitle number="2" title="품목별 배출량 기록" />
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>날짜</TableCell>
                  <TableCell align="right">PET (kg)</TableCell>
                  <TableCell align="right">혼합 PET (kg)</TableCell>
                  <TableCell align="right">알루미늄 (kg)</TableCell>
                  <TableCell align="right">파지 (kg)</TableCell>
                  <TableCell align="right">합계 (kg)</TableCell>
                  <TableCell align="center">배출시간</TableCell>
                  <TableCell align="center">사진</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(loc.dailyRecords || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      해당 월 수거 기록 없음
                    </TableCell>
                  </TableRow>
                ) : (
                  loc.dailyRecords.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{r.date}</TableCell>
                      <TableCell align="right">{fmt(r.petWeight)}</TableCell>
                      <TableCell align="right">
                        {fmt(r.mixedPetWeight)}
                      </TableCell>
                      <TableCell align="right">
                        {fmt(r.aluminumWeight)}
                      </TableCell>
                      <TableCell align="right">{fmt(r.paperWeight)}</TableCell>
                      <TableCell align="right">{fmt(r.totalWeight)}</TableCell>
                      <TableCell align="center">
                        {r.collectedTime || "-"}
                      </TableCell>
                      <TableCell align="center">
                        {r.hasPhoto ? "✅" : "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 장소별 월간 합계 */}
          {loc.monthSummary && (
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                [{loc.locationName}] 월간 합계
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    PET
                  </Typography>
                  <Typography fontWeight={600}>
                    {fmtKg(loc.monthSummary.totalPet)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    혼합 PET
                  </Typography>
                  <Typography fontWeight={600}>
                    {fmtKg(loc.monthSummary.totalMixedPet)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    알루미늄
                  </Typography>
                  <Typography fontWeight={600}>
                    {fmtKg(loc.monthSummary.totalAluminum)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    파지
                  </Typography>
                  <Typography fontWeight={600}>
                    {fmtKg(loc.monthSummary.totalPaper)}
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 1 }} />
              <Typography fontWeight={700}>
                총 배출량: {fmtKg(loc.monthSummary.grandTotal)} | 수거 횟수:{" "}
                {loc.monthSummary.collectionCount}회
              </Typography>
            </Box>
          )}
        </Box>
      ))}

      {/* 전체 월간 합계 */}
      <SectionTitle number="3" title="전체 월간 합계" />
      <TotalSummaryBox summary={ts} />

      {/* 정산 요약 */}
      <SectionTitle number="4" title="정산 요약" />
      <SettlementTable summary={ts} />

      {/* 확인란 */}
      <SectionTitle number="5" title="확인" />
      <ConfirmSection />
    </Box>
  );
}

// ═══════════════════════════════════════════════════
// 2) 고물량 세부형 미리보기
// ═══════════════════════════════════════════════════
function LogisticsPreview({ data }) {
  const ts = data.totalSummary || {};
  return (
    <Box>
      {/* 기본 정보 */}
      <SectionTitle number="1" title="기본 정보" />
      <InfoGrid
        items={[
          { label: "담당자", value: data.collectorName || "전체" },
          { label: "연락처", value: data.collectorPhone || "-" },
          {
            label: "기록 기간",
            value: `${data.year}년 ${data.month}월`,
          },
          { label: "수거 장소 수", value: `${data.locations?.length || 0}곳` },
          {
            label: "월 총 배출량",
            value: fmtKg(ts.grandTotal),
          },
        ]}
      />

      {/* 장소별 세부 품목 기록 */}
      {(data.locations || []).map((loc) => (
        <Box key={loc.locationId} sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            📍 {loc.locationName} ({loc.address})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            소유자: {loc.ownerName} | 연락처: {loc.ownerPhone || "-"} | 수거
            요일: {loc.locationDay}
          </Typography>

          <SectionTitle number="2" title="세부 품목 기록" />
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>날짜</TableCell>
                  <TableCell align="right">PET (kg)</TableCell>
                  <TableCell align="right">혼합 PET (kg)</TableCell>
                  <TableCell align="right">알루미늄 (kg)</TableCell>
                  <TableCell align="right">파지 (kg)</TableCell>
                  <TableCell align="right">합계 (kg)</TableCell>
                  <TableCell align="center">배출시간</TableCell>
                  <TableCell align="center">사진</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(loc.dailyRecords || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      해당 월 수거 기록 없음
                    </TableCell>
                  </TableRow>
                ) : (
                  loc.dailyRecords.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{r.date}</TableCell>
                      <TableCell align="right">{fmt(r.petWeight)}</TableCell>
                      <TableCell align="right">
                        {fmt(r.mixedPetWeight)}
                      </TableCell>
                      <TableCell align="right">
                        {fmt(r.aluminumWeight)}
                      </TableCell>
                      <TableCell align="right">{fmt(r.paperWeight)}</TableCell>
                      <TableCell align="right">
                        <strong>{fmt(r.totalWeight)}</strong>
                      </TableCell>
                      <TableCell align="center">
                        {r.collectedTime || "-"}
                      </TableCell>
                      <TableCell align="center">
                        {r.hasPhoto ? "✅" : "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 장소별 합계 */}
          {loc.monthSummary && (
            <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1, mb: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                [{loc.locationName}] 총 배출량:{" "}
                {fmtKg(loc.monthSummary.grandTotal)} | 수거{" "}
                {loc.monthSummary.collectionCount}회
              </Typography>
            </Box>
          )}
        </Box>
      ))}

      {/* 월간 요약 */}
      <SectionTitle number="3" title="월간 요약" />
      <Box sx={{ p: 2, bgcolor: "primary.50", borderRadius: 1, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="body2" color="text.secondary">
              총 배출량
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {fmtKg(ts.grandTotal)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="body2" color="text.secondary">
              품질 등급
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {getQualityGrade(ts)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="body2" color="text.secondary">
              수거 횟수
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {ts.collectionCount || 0}회
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="body2" color="text.secondary">
              전월 대비
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              color={
                ts.changePercent > 0
                  ? "success.main"
                  : ts.changePercent < 0
                    ? "error.main"
                    : "text.primary"
              }
            >
              {ts.changePercent != null
                ? `${ts.changePercent > 0 ? "+" : ""}${ts.changePercent.toFixed(1)}%`
                : "-"}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* 정산 요약 */}
      <SectionTitle number="4" title="정산 요약" />
      <SettlementTable summary={ts} />

      {/* 확인란 */}
      <SectionTitle number="5" title="확인" />
      <ConfirmSection />
    </Box>
  );
}

// ═══════════════════════════════════════════════════
// 3) 지자체 제출용 미리보기
// ═══════════════════════════════════════════════════
function GovernmentPreview({ data }) {
  const ts = data.totalSummary || {};
  return (
    <Box>
      {/* 사업장 정보 */}
      <SectionTitle number="1" title="사업장 정보" />
      <InfoGrid
        items={[
          { label: "업체명", value: "이룸그린테크" },
          { label: "대표자", value: data.collectorName || "-" },
          {
            label: "기록 기간",
            value: `${data.year}년 ${data.month}월`,
          },
          { label: "관리 장소 수", value: `${data.locations?.length || 0}곳` },
        ]}
      />

      {/* 재활용품 배출 및 수거 실적 요약 */}
      <SectionTitle
        number="2"
        title="재활용품 배출 및 수거 실적 요약 (월 기준)"
      />
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>항목</TableCell>
              <TableCell align="right">수량 (kg)</TableCell>
              <TableCell align="right">전월 대비 증감</TableCell>
              <TableCell>비고</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>PET</TableCell>
              <TableCell align="right">{fmt(ts.totalPet)}</TableCell>
              <TableCell align="right">-</TableCell>
              <TableCell>일반 PET</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>혼합 PET</TableCell>
              <TableCell align="right">{fmt(ts.totalMixedPet)}</TableCell>
              <TableCell align="right">-</TableCell>
              <TableCell>유색/혼합</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>알루미늄</TableCell>
              <TableCell align="right">{fmt(ts.totalAluminum)}</TableCell>
              <TableCell align="right">-</TableCell>
              <TableCell>캔류</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>파지</TableCell>
              <TableCell align="right">{fmt(ts.totalPaper)}</TableCell>
              <TableCell align="right">-</TableCell>
              <TableCell>종이류</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell>
                <strong>총량</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{fmt(ts.grandTotal)}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>
                  {ts.changePercent != null
                    ? `${ts.changePercent > 0 ? "+" : ""}${ts.changePercent.toFixed(1)}%`
                    : "-"}
                </strong>
              </TableCell>
              <TableCell>
                전월 총량: {fmtKg(ts.prevMonthTotal)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* 수거 현황 */}
      <SectionTitle number="3" title="수거 현황" />
      <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1, mb: 2 }}>
        <Typography>월 수거 횟수: {ts.collectionCount || 0}회</Typography>
        <Typography>담당 수거업체: 이룸그린테크</Typography>
      </Box>

      {/* 장소별 세부 내역 */}
      <SectionTitle number="4" title="관리 장소별 세부 내역" />
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>장소명</TableCell>
              <TableCell>주소</TableCell>
              <TableCell align="right">PET</TableCell>
              <TableCell align="right">혼합 PET</TableCell>
              <TableCell align="right">알루미늄</TableCell>
              <TableCell align="right">파지</TableCell>
              <TableCell align="right">소계 (kg)</TableCell>
              <TableCell align="right">수거 횟수</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.locations || []).map((loc) => (
              <TableRow key={loc.locationId}>
                <TableCell>{loc.locationName}</TableCell>
                <TableCell>{loc.address}</TableCell>
                <TableCell align="right">
                  {fmt(loc.monthSummary?.totalPet)}
                </TableCell>
                <TableCell align="right">
                  {fmt(loc.monthSummary?.totalMixedPet)}
                </TableCell>
                <TableCell align="right">
                  {fmt(loc.monthSummary?.totalAluminum)}
                </TableCell>
                <TableCell align="right">
                  {fmt(loc.monthSummary?.totalPaper)}
                </TableCell>
                <TableCell align="right">
                  <strong>{fmt(loc.monthSummary?.grandTotal)}</strong>
                </TableCell>
                <TableCell align="right">
                  {loc.monthSummary?.collectionCount || 0}회
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell colSpan={2}>
                <strong>합계</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{fmt(ts.totalPet)}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{fmt(ts.totalMixedPet)}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{fmt(ts.totalAluminum)}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{fmt(ts.totalPaper)}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{fmt(ts.grandTotal)}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{ts.collectionCount || 0}회</strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* 종합 평가 */}
      <SectionTitle number="5" title="지자체 제출용 종합 평가" />
      <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="body2" color="text.secondary">
              배출 안정성
            </Typography>
            <Chip
              label={ts.collectionCount >= 4 ? "양호" : ts.collectionCount >= 2 ? "보통" : "개선필요"}
              color={ts.collectionCount >= 4 ? "success" : ts.collectionCount >= 2 ? "warning" : "error"}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="body2" color="text.secondary">
              품질 등급
            </Typography>
            <Chip
              label={getQualityGrade(ts)}
              color={
                getQualityGrade(ts) === "A"
                  ? "success"
                  : getQualityGrade(ts) === "B"
                    ? "warning"
                    : "default"
              }
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="body2" color="text.secondary">
              전월 대비
            </Typography>
            <Typography fontWeight={600}>
              {ts.changePercent != null
                ? `${ts.changePercent > 0 ? "+" : ""}${ts.changePercent.toFixed(1)}%`
                : "-"}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="body2" color="text.secondary">
              환경개선 기여도
            </Typography>
            <Chip
              label={ts.grandTotal >= 100 ? "높음" : ts.grandTotal >= 30 ? "보통" : "낮음"}
              color={ts.grandTotal >= 100 ? "success" : ts.grandTotal >= 30 ? "warning" : "default"}
              size="small"
            />
          </Grid>
        </Grid>
      </Box>

      {/* 확인란 */}
      <SectionTitle number="6" title="확인" />
      <ConfirmSection />
    </Box>
  );
}

// ═══════════════════════════════════════════════════
// 공통 서브 컴포넌트
// ═══════════════════════════════════════════════════

function SectionTitle({ number, title }) {
  return (
    <Typography
      variant="subtitle1"
      fontWeight={700}
      sx={{
        mt: 3,
        mb: 1.5,
        color: "primary.main",
        borderBottom: "2px solid",
        borderColor: "primary.main",
        pb: 0.5,
        display: "inline-block",
      }}
    >
      {number}. {title}
    </Typography>
  );
}

function InfoGrid({ items }) {
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "grey.50",
        borderRadius: 1,
        mb: 2,
      }}
    >
      <Grid container spacing={2}>
        {items.map((item, idx) => (
          <Grid key={idx} size={{ xs: 6, sm: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {item.label}
            </Typography>
            <Typography fontWeight={600}>{item.value}</Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function TotalSummaryBox({ summary }) {
  if (!summary) return null;
  return (
    <Box
      sx={{
        p: 2.5,
        bgcolor: "#e8f4f8",
        borderRadius: 1,
        border: "1px solid #b3dce8",
        mb: 2,
      }}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 2.4 }}>
          <Typography variant="body2" color="text.secondary">
            총 PET
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {fmtKg(summary.totalPet)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6, sm: 2.4 }}>
          <Typography variant="body2" color="text.secondary">
            총 혼합 PET
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {fmtKg(summary.totalMixedPet)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6, sm: 2.4 }}>
          <Typography variant="body2" color="text.secondary">
            총 알루미늄
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {fmtKg(summary.totalAluminum)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6, sm: 2.4 }}>
          <Typography variant="body2" color="text.secondary">
            총 파지
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {fmtKg(summary.totalPaper)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6, sm: 2.4 }}>
          <Typography variant="body2" color="text.secondary">
            총 배출량
          </Typography>
          <Typography variant="h5" fontWeight={800} color="primary.main">
            {fmtKg(summary.grandTotal)}
          </Typography>
        </Grid>
      </Grid>
      <Divider sx={{ my: 1.5 }} />
      <Typography>
        월 수거 횟수: <strong>{summary.collectionCount || 0}회</strong>
        {summary.changePercent != null && (
          <>
            {" "}
            | 전월 대비:{" "}
            <strong
              style={{
                color:
                  summary.changePercent > 0
                    ? "#27ae60"
                    : summary.changePercent < 0
                      ? "#e74c3c"
                      : "inherit",
              }}
            >
              {summary.changePercent > 0 ? "+" : ""}
              {summary.changePercent.toFixed(1)}%
            </strong>
          </>
        )}
      </Typography>
    </Box>
  );
}

function SettlementTable({ summary }) {
  if (!summary) return null;
  const items = [
    { name: "PET", weight: summary.totalPet },
    { name: "혼합 PET", weight: summary.totalMixedPet },
    { name: "알루미늄", weight: summary.totalAluminum },
    { name: "파지", weight: summary.totalPaper },
  ];
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>품목</TableCell>
            <TableCell align="right">총 배출량 (kg)</TableCell>
            <TableCell align="right">단가 (원/kg)</TableCell>
            <TableCell align="right">합계 (원)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.name}>
              <TableCell>{item.name}</TableCell>
              <TableCell align="right">{fmt(item.weight)}</TableCell>
              <TableCell align="right" sx={{ color: "text.secondary" }}>
                -
              </TableCell>
              <TableCell align="right" sx={{ color: "text.secondary" }}>
                -
              </TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ bgcolor: "grey.50" }}>
            <TableCell>
              <strong>합계</strong>
            </TableCell>
            <TableCell align="right">
              <strong>{fmt(summary.grandTotal)}</strong>
            </TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">
              <strong>-</strong>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Box sx={{ p: 1.5, bgcolor: "grey.50" }}>
        <Typography variant="body2" color="text.secondary">
          * 단가 설정 기능은 추후 업데이트 예정입니다. 단가 입력 시 자동 정산됩니다.
        </Typography>
      </Box>
    </TableContainer>
  );
}

function ConfirmSection() {
  return (
    <Box
      sx={{
        p: 2,
        border: "1px dashed",
        borderColor: "grey.400",
        borderRadius: 1,
        mb: 2,
      }}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Typography variant="body2" color="text.secondary">
            이룸그린테크 담당자
          </Typography>
          <Box
            sx={{
              borderBottom: "1px solid #ccc",
              mt: 1,
              pb: 2,
              minWidth: 120,
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Typography variant="body2" color="text.secondary">
            날짜
          </Typography>
          <Box
            sx={{
              borderBottom: "1px solid #ccc",
              mt: 1,
              pb: 2,
              minWidth: 120,
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Typography variant="body2" color="text.secondary">
            확인 (서명)
          </Typography>
          <Box
            sx={{
              borderBottom: "1px solid #ccc",
              mt: 1,
              pb: 2,
              minWidth: 120,
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

// ═══════════════════════════════════════════════════
// PDF 생성 함수들
// ═══════════════════════════════════════════════════

function generateStoreReport(doc, data, yPos, pageWidth, tableFont) {
  const ts = data.totalSummary || {};

  // 1. 기본 정보
  doc.setFont(tableFont, "bold");
  doc.setFontSize(12);
  doc.setTextColor(42, 154, 198);
  doc.text("1. 기본 정보", 14, yPos);
  yPos += 8;

  doc.setFont(tableFont, "normal");
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text(`담당자: ${data.collectorName || "전체"}`, 14, yPos);
  doc.text(`연락처: ${data.collectorPhone || "-"}`, 105, yPos);
  yPos += 6;
  doc.text(`기간: ${data.year}년 ${String(data.month).padStart(2, "0")}월`, 14, yPos);
  doc.text(`수거 장소: ${data.locations?.length || 0}곳`, 105, yPos);
  yPos += 10;

  // 각 장소별 테이블
  (data.locations || []).forEach((loc) => {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFont(tableFont, "bold");
    doc.setFontSize(11);
    doc.setTextColor(42, 154, 198);
    doc.text(`[${loc.locationName}]`, 14, yPos);
    yPos += 5;
    doc.setFont(tableFont, "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`주소: ${loc.address} | 소유자: ${loc.ownerName}`, 14, yPos);
    yPos += 7;

    // 2. 배출량 테이블
    const headers = [["날짜", "PET(kg)", "혼합PET(kg)", "알루미늄(kg)", "파지(kg)", "합계(kg)", "시간"]];
    const rows = (loc.dailyRecords || []).map((r) => [
      r.date,
      fmt(r.petWeight),
      fmt(r.mixedPetWeight),
      fmt(r.aluminumWeight),
      fmt(r.paperWeight),
      fmt(r.totalWeight),
      r.collectedTime || "-",
    ]);

    if (rows.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: headers,
        body: rows,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 2, font: tableFont },
        headStyles: { fillColor: [42, 154, 198], textColor: 255, font: tableFont, fontStyle: "bold" },
        margin: { left: 14, right: 14 },
      });
      yPos = doc.lastAutoTable.finalY + 5;
    } else {
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text("해당 월 수거 기록 없음", 14, yPos);
      yPos += 7;
    }

    // 장소별 소계
    if (loc.monthSummary) {
      doc.setFont(tableFont, "normal");
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text(
        `소계: ${fmt(loc.monthSummary.grandTotal)} kg (${loc.monthSummary.collectionCount}회 수거)`,
        14,
        yPos
      );
      yPos += 8;
    }
  });

  // 3. 월간 합계
  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }
  doc.setFont(tableFont, "bold");
  doc.setFontSize(12);
  doc.setTextColor(42, 154, 198);
  doc.text("2. 전체 월간 합계", 14, yPos);
  yPos += 8;

  autoTable(doc, {
    startY: yPos,
    head: [["품목", "중량 (kg)"]],
    body: [
      ["PET", fmt(ts.totalPet)],
      ["혼합 PET", fmt(ts.totalMixedPet)],
      ["알루미늄", fmt(ts.totalAluminum)],
      ["파지", fmt(ts.totalPaper)],
      ["총 배출량", fmt(ts.grandTotal)],
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 3, font: tableFont },
    headStyles: { fillColor: [42, 154, 198], textColor: 255, font: tableFont, fontStyle: "bold" },
    margin: { left: 14, right: 14 },
  });
  yPos = doc.lastAutoTable.finalY + 5;

  doc.setFont(tableFont, "normal");
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text(`월 수거 횟수: ${ts.collectionCount || 0}회`, 14, yPos);
  yPos += 10;

  // 3. 정산 요약
  if (yPos > 230) { doc.addPage(); yPos = 20; }
  doc.setFont(tableFont, "bold");
  doc.setFontSize(12);
  doc.setTextColor(42, 154, 198);
  doc.text("3. 정산 요약", 14, yPos);
  yPos += 8;

  autoTable(doc, {
    startY: yPos,
    head: [["품목", "총 배출량 (kg)", "단가 (원/kg)", "합계 (원)"]],
    body: [
      ["PET", fmt(ts.totalPet), "-", "-"],
      ["혼합 PET", fmt(ts.totalMixedPet), "-", "-"],
      ["알루미늄", fmt(ts.totalAluminum), "-", "-"],
      ["파지", fmt(ts.totalPaper), "-", "-"],
      ["합계", fmt(ts.grandTotal), "-", "-"],
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 3, font: tableFont },
    headStyles: { fillColor: [42, 154, 198], textColor: 255, font: tableFont, fontStyle: "bold" },
    margin: { left: 14, right: 14 },
  });
  yPos = doc.lastAutoTable.finalY + 5;

  doc.setFont(tableFont, "normal");
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text("* 단가 설정 기능은 추후 업데이트 예정입니다.", 14, yPos);
  yPos += 10;

  return yPos;
}

function generateLogisticsReport(doc, data, yPos, pageWidth, tableFont) {
  const ts = data.totalSummary || {};

  doc.setFont(tableFont, "bold");
  doc.setFontSize(12);
  doc.setTextColor(42, 154, 198);
  doc.text("1. 기본 정보", 14, yPos);
  yPos += 8;

  doc.setFont(tableFont, "normal");
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text(`담당자: ${data.collectorName || "전체"}`, 14, yPos);
  doc.text(`월 총 배출량: ${fmt(ts.grandTotal)} kg`, 105, yPos);
  yPos += 6;
  doc.text(`기간: ${data.year}년 ${String(data.month).padStart(2, "0")}월`, 14, yPos);
  doc.text(`품질 등급: ${getQualityGrade(ts)}`, 105, yPos);
  yPos += 10;

  // 장소별 세부 기록
  (data.locations || []).forEach((loc) => {
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFont(tableFont, "bold");
    doc.setFontSize(11);
    doc.setTextColor(42, 154, 198);
    doc.text(`[${loc.locationName}]`, 14, yPos);
    yPos += 5;
    doc.setFont(tableFont, "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`주소: ${loc.address} | 소유자: ${loc.ownerName} | 연락처: ${loc.ownerPhone || "-"}`, 14, yPos);
    yPos += 7;

    const headers = [["날짜", "PET(kg)", "혼합PET(kg)", "알루미늄(kg)", "파지(kg)", "합계(kg)", "시간", "사진"]];
    const rows = (loc.dailyRecords || []).map((r) => [
      r.date,
      fmt(r.petWeight),
      fmt(r.mixedPetWeight),
      fmt(r.aluminumWeight),
      fmt(r.paperWeight),
      fmt(r.totalWeight),
      r.collectedTime || "-",
      r.hasPhoto ? "O" : "-",
    ]);

    if (rows.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: headers,
        body: rows,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 2, font: tableFont },
        headStyles: { fillColor: [42, 154, 198], textColor: 255, font: tableFont, fontStyle: "bold" },
        margin: { left: 14, right: 14 },
      });
      yPos = doc.lastAutoTable.finalY + 5;
    } else {
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text("해당 월 수거 기록 없음", 14, yPos);
      yPos += 7;
    }

    if (loc.monthSummary) {
      doc.setFont(tableFont, "normal");
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text(
        `소계: ${fmt(loc.monthSummary.grandTotal)} kg | 수거 ${loc.monthSummary.collectionCount}회`,
        14,
        yPos
      );
      yPos += 8;
    }
  });

  // 월간 요약
  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }
  doc.setFont(tableFont, "bold");
  doc.setFontSize(12);
  doc.setTextColor(42, 154, 198);
  doc.text("2. 월간 요약", 14, yPos);
  yPos += 8;

  autoTable(doc, {
    startY: yPos,
    head: [["품목", "중량 (kg)", "비고"]],
    body: [
      ["PET", fmt(ts.totalPet), ""],
      ["혼합 PET", fmt(ts.totalMixedPet), ""],
      ["알루미늄", fmt(ts.totalAluminum), ""],
      ["파지", fmt(ts.totalPaper), ""],
      ["총 배출량", fmt(ts.grandTotal), `${ts.collectionCount}회 수거`],
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 3, font: tableFont },
    headStyles: { fillColor: [42, 154, 198], textColor: 255, font: tableFont, fontStyle: "bold" },
    margin: { left: 14, right: 14 },
  });
  yPos = doc.lastAutoTable.finalY + 5;

  doc.setFont(tableFont, "normal");
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text(`품질 등급: ${getQualityGrade(ts)}`, 14, yPos);
  if (ts.changePercent != null) {
    doc.text(`전월 대비: ${ts.changePercent > 0 ? "+" : ""}${ts.changePercent.toFixed(1)}%`, 80, yPos);
  }
  yPos += 10;

  // 정산 요약
  if (yPos > 230) { doc.addPage(); yPos = 20; }
  doc.setFont(tableFont, "bold");
  doc.setFontSize(12);
  doc.setTextColor(42, 154, 198);
  doc.text("3. 정산 요약", 14, yPos);
  yPos += 8;

  autoTable(doc, {
    startY: yPos,
    head: [["품목", "총 배출량 (kg)", "단가 (원/kg)", "합계 (원)"]],
    body: [
      ["PET", fmt(ts.totalPet), "-", "-"],
      ["혼합 PET", fmt(ts.totalMixedPet), "-", "-"],
      ["알루미늄", fmt(ts.totalAluminum), "-", "-"],
      ["파지", fmt(ts.totalPaper), "-", "-"],
      ["합계", fmt(ts.grandTotal), "-", "-"],
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 3, font: tableFont },
    headStyles: { fillColor: [42, 154, 198], textColor: 255, font: tableFont, fontStyle: "bold" },
    margin: { left: 14, right: 14 },
  });
  yPos = doc.lastAutoTable.finalY + 5;

  doc.setFont(tableFont, "normal");
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text("* 단가 설정 기능은 추후 업데이트 예정입니다.", 14, yPos);
  yPos += 10;

  return yPos;
}

function generateGovernmentReport(doc, data, yPos, pageWidth, tableFont) {
  const ts = data.totalSummary || {};

  // 1. 사업장 정보
  doc.setFont(tableFont, "bold");
  doc.setFontSize(12);
  doc.setTextColor(42, 154, 198);
  doc.text("1. 사업장 정보", 14, yPos);
  yPos += 8;

  doc.setFont(tableFont, "normal");
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text("업체명: 이룸그린테크", 14, yPos);
  yPos += 6;
  doc.text(`대표자: ${data.collectorName || "-"}`, 14, yPos);
  yPos += 6;
  doc.text(`기간: ${data.year}년 ${String(data.month).padStart(2, "0")}월`, 14, yPos);
  doc.text(`관리 장소: ${data.locations?.length || 0}곳`, 105, yPos);
  yPos += 10;

  // 2. 실적 요약
  doc.setFont(tableFont, "bold");
  doc.setFontSize(12);
  doc.setTextColor(42, 154, 198);
  doc.text("2. 재활용품 배출 및 수거 실적 요약 (월 기준)", 14, yPos);
  yPos += 8;

  autoTable(doc, {
    startY: yPos,
    head: [["항목", "수량 (kg)", "전월 대비", "비고"]],
    body: [
      ["PET", fmt(ts.totalPet), "-", "일반 PET"],
      ["혼합 PET", fmt(ts.totalMixedPet), "-", "유색/혼합"],
      ["알루미늄", fmt(ts.totalAluminum), "-", "캔류"],
      ["파지", fmt(ts.totalPaper), "-", "종이류"],
      [
        "총량",
        fmt(ts.grandTotal),
        ts.changePercent != null ? `${ts.changePercent > 0 ? "+" : ""}${ts.changePercent.toFixed(1)}%` : "-",
        `전월: ${fmt(ts.prevMonthTotal)} kg`,
      ],
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 3, font: tableFont },
    headStyles: { fillColor: [42, 154, 198], textColor: 255, font: tableFont, fontStyle: "bold" },
    margin: { left: 14, right: 14 },
  });
  yPos = doc.lastAutoTable.finalY + 10;

  // 3. 장소별 세부
  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }
  doc.setFont(tableFont, "bold");
  doc.setFontSize(12);
  doc.setTextColor(42, 154, 198);
  doc.text("3. 관리 장소별 세부 내역", 14, yPos);
  yPos += 8;

  const locHeaders = [["장소명", "주소", "PET", "혼합PET", "알루미늄", "파지", "소계(kg)", "횟수"]];
  const locRows = (data.locations || []).map((loc) => [
    loc.locationName || "-",
    (loc.address || "").substring(0, 20),
    fmt(loc.monthSummary?.totalPet),
    fmt(loc.monthSummary?.totalMixedPet),
    fmt(loc.monthSummary?.totalAluminum),
    fmt(loc.monthSummary?.totalPaper),
    fmt(loc.monthSummary?.grandTotal),
    `${loc.monthSummary?.collectionCount || 0}회`,
  ]);

  autoTable(doc, {
    startY: yPos,
    head: locHeaders,
    body: locRows,
    theme: "grid",
    styles: { fontSize: 7, cellPadding: 2, font: tableFont },
    headStyles: { fillColor: [42, 154, 198], textColor: 255, font: tableFont, fontStyle: "bold" },
    margin: { left: 14, right: 14 },
  });
  yPos = doc.lastAutoTable.finalY + 10;

  // 4. 수거 현황
  if (yPos > 250) { doc.addPage(); yPos = 20; }
  doc.setFont(tableFont, "bold");
  doc.setFontSize(12);
  doc.setTextColor(42, 154, 198);
  doc.text("4. 수거 현황", 14, yPos);
  yPos += 8;

  doc.setFont(tableFont, "normal");
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text(`월 수거 횟수: ${ts.collectionCount || 0}회`, 14, yPos);
  yPos += 6;
  doc.text("담당 수거업체: 이룸그린테크", 14, yPos);
  yPos += 10;

  // 5. 종합평가
  if (yPos > 250) { doc.addPage(); yPos = 20; }
  doc.setFont(tableFont, "bold");
  doc.setFontSize(12);
  doc.setTextColor(42, 154, 198);
  doc.text("5. 지자체 제출용 종합 평가", 14, yPos);
  yPos += 8;

  const stability = ts.collectionCount >= 4 ? "양호" : ts.collectionCount >= 2 ? "보통" : "개선필요";
  const contribution = ts.grandTotal >= 100 ? "높음" : ts.grandTotal >= 30 ? "보통" : "낮음";

  autoTable(doc, {
    startY: yPos,
    head: [["평가 항목", "결과"]],
    body: [
      ["배출 안정성", stability],
      ["품질 등급", getQualityGrade(ts)],
      ["전월 대비 증감", ts.changePercent != null ? `${ts.changePercent > 0 ? "+" : ""}${ts.changePercent.toFixed(1)}%` : "-"],
      ["환경개선 기여도", contribution],
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 3, font: tableFont },
    headStyles: { fillColor: [42, 154, 198], textColor: 255, font: tableFont, fontStyle: "bold" },
    margin: { left: 14, right: 14 },
  });
  yPos = doc.lastAutoTable.finalY + 10;

  return yPos;
}

export default ReportPage;
